import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdCheckCircle,
  MdCancel,
  MdVisibility,
  MdFilterList,
  MdFlight,
  MdFlightTakeoff,
  MdWarning,
  MdOutlineEngineering,
  MdWork,
  MdRefresh,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

type ListingCategory =
  | "ALL"
  | "AIRCRAFT_SALE_LEASE_AVAILABLE"
  | "AIRCRAFT_SALE_LEASE_WANTED"
  | "WET_LEASE_AVAILABLE"
  | "WET_LEASE_WANTED"
  | "WET_LEASE_AOG"
  | "ENGINE_AVAILABLE"
  | "ENGINE_WANTED"
  | "CHARTER_AVAILABLE"
  | "CHARTER_WANTED"
  | "BUSINESS_JET_AVAILABLE"
  | "BUSINESS_JET_WANTED";

interface UnifiedPendingListing {
  id: number;
  category: ListingCategory;
  categoryLabel: string;
  moduleGroup: "Aircraft" | "Wet Lease" | "Engine" | "Charter" | "Business Jet";
  title: string;
  subTitle?: string;
  companyName?: string;
  companyId?: number;
  userName?: string;
  createdDate?: string;
  status: number; // 0 = Pending
  raw: any;
}

export const PendingListingsPage: React.FC = () => {
  const { setPageMenus } = useOutletContext<any>();
  const api = useMemo(() => createApi(), []);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [listings, setListings] = useState<UnifiedPendingListing[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
  const [selectedDetail, setSelectedDetail] = useState<UnifiedPendingListing | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    listing: UnifiedPendingListing;
    status: 1 | 2; // 1 = Approved, 2 = Rejected
  } | null>(null);

  const loadPendingListings = useCallback(async () => {
    setLoading(true);
    try {
      const results: UnifiedPendingListing[] = [];

      // 1. Aircraft Sale/Lease Available
      try {
        const r: any = await api.aircraftsaleleaseAvailable.getAll();
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "AIRCRAFT_SALE_LEASE_AVAILABLE",
            categoryLabel: "Aircraft Sale/Lease Available",
            moduleGroup: "Aircraft",
            title: `${x.aircraftType?.name ?? "Aircraft"} (${x.tradeType === 1 ? "Sale" : "Lease"})`,
            subTitle: `MSN: ${x.msn ?? "-"} | YOM: ${x.yom ?? "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Aircraft Sale/Lease Available", e);
      }

      // 2. Aircraft Sale/Lease Wanted
      try {
        const r: any = await api.aircraftsaleleaseWanted.getAll();
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "AIRCRAFT_SALE_LEASE_WANTED",
            categoryLabel: "Aircraft Sale/Lease Wanted",
            moduleGroup: "Aircraft",
            title: `${x.aircraftType?.name ?? "Aircraft Wanted"} (${x.tradeType === 1 ? "Buy" : "Lease"})`,
            subTitle: `Preferred YOM: ${x.minYOM ?? "-"} - ${x.maxYOM ?? "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Aircraft Sale/Lease Wanted", e);
      }

      // 3. Wet Lease Available
      try {
        const r: any = await api.wetLeaseAvailable.getAll();
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "WET_LEASE_AVAILABLE",
            categoryLabel: "Wet Lease Available",
            moduleGroup: "Wet Lease",
            title: `${x.aircraftType?.name ?? "Wet Lease"} Available`,
            subTitle: `MSN: ${x.msn ?? "-"} | Base: ${x.baseAirport?.iataCode ?? "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Wet Lease Available", e);
      }

      // 4. Wet Lease Wanted
      try {
        const r: any = await api.wetLeaseWanted.getAll();
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "WET_LEASE_WANTED",
            categoryLabel: "Wet Lease Wanted",
            moduleGroup: "Wet Lease",
            title: `${x.aircraftType?.name ?? "Wet Lease"} Wanted`,
            subTitle: `Routing: ${x.originAirport?.iataCode ?? "-"} -> ${x.destinationAirport?.iataCode ?? "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Wet Lease Wanted", e);
      }

      // 5. Wet Lease AOG
      try {
        const r: any = await api.wetLeaseAOG.getAll();
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "WET_LEASE_AOG",
            categoryLabel: "URGENT AOG",
            moduleGroup: "Wet Lease",
            title: `🚨 AOG: ${x.aircraftType?.name ?? "Aircraft"}`,
            subTitle: `Location: ${x.airport?.name ?? x.airport?.iataCode ?? "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Wet Lease AOG", e);
      }

      // 6. Engine Available
      try {
        const r: any = await api.engineSaleLeaseAvailable.getAll({ isAll: true });
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "ENGINE_AVAILABLE",
            categoryLabel: "Engine Available",
            moduleGroup: "Engine",
            title: `${x.engineType?.name ?? "Engine"} (${x.tradeType === 1 ? "Sale" : "Lease"})`,
            subTitle: `ESN: ${x.esn ?? "-"} | Location: ${x.airport?.iataCode ?? "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Engine Available", e);
      }

      // 7. Engine Wanted
      try {
        const r: any = await api.engineSaleLeaseWanted.getAll({ isAll: true });
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "ENGINE_WANTED",
            categoryLabel: "Engine Wanted",
            moduleGroup: "Engine",
            title: `${x.engineType?.name ?? "Engine Wanted"}`,
            subTitle: `Target Date: ${x.deliveryDate ? new Date(x.deliveryDate).toLocaleDateString() : "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Engine Wanted", e);
      }

      // 8. Charter Available
      try {
        const r: any = await api.aircraftCharterAvailable.getAll({ isAll: true });
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "CHARTER_AVAILABLE",
            categoryLabel: "Charter Available",
            moduleGroup: "Charter",
            title: `${x.aircraftType?.name ?? "Charter Aircraft"} Available`,
            subTitle: `Capacity: ${x.capacity ?? "-"} Seats`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Charter Available", e);
      }

      // 9. Charter Wanted
      try {
        const r: any = await api.aircraftCharterWanted.getAll({ isAll: true });
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "CHARTER_WANTED",
            categoryLabel: "Charter Wanted",
            moduleGroup: "Charter",
            title: `Charter Wanted: ${x.aircraftType?.name ?? "Aircraft"}`,
            subTitle: `Pax: ${x.paxCount ?? "-"} | Route: ${x.originAirport?.iataCode ?? "-"} -> ${x.destinationAirport?.iataCode ?? "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Charter Wanted", e);
      }

      // 10. Business Jet Available
      try {
        const r: any = await api.businessJetAvailable.getAll({ isAll: true });
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "BUSINESS_JET_AVAILABLE",
            categoryLabel: "Business Jet Available",
            moduleGroup: "Business Jet",
            title: `${x.aircraftType?.name ?? "Business Jet"} Available`,
            subTitle: `Base: ${x.airport?.iataCode ?? "-"} | Pax: ${x.paxCapacity ?? "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Business Jet Available", e);
      }

      // 11. Business Jet Wanted
      try {
        const r: any = await api.businessJetWanted.getAll({ isAll: true });
        const data = (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === 0);
        data.forEach((x: any) => {
          results.push({
            id: x.id,
            category: "BUSINESS_JET_WANTED",
            categoryLabel: "Business Jet Wanted",
            moduleGroup: "Business Jet",
            title: `${x.aircraftType?.name ?? "Business Jet"} Wanted`,
            subTitle: `Route: ${x.originAirport?.iataCode ?? "-"} -> ${x.destinationAirport?.iataCode ?? "-"}`,
            companyName: x.company?.companyName ?? "N/A",
            companyId: x.companyId,
            userName: x.user?.fullName ?? x.user?.email,
            createdDate: x.createdDate,
            status: x.confirmStatus,
            raw: x,
          });
        });
      } catch (e) {
        console.error("Error loading Business Jet Wanted", e);
      }

      // Sort descending by created date
      results.sort((a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime());

      setListings(results);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Approval Center",
        items: [{ label: "Pending Listings", route: "/dashboard/pending-listings" }],
      },
    ]);
    loadPendingListings();
    return () => setPageMenus([]);
  }, [loadPendingListings, setPageMenus]);

  const handleUpdateStatus = async (listing: UnifiedPendingListing, status: 1 | 2) => {
    setActionLoading(listing.id);
    try {
      switch (listing.category) {
        case "AIRCRAFT_SALE_LEASE_AVAILABLE":
          await api.aircraftsaleleaseAvailable.confirmStatus(listing.id, status);
          break;
        case "AIRCRAFT_SALE_LEASE_WANTED":
          await api.aircraftsaleleaseWanted.confirmStatus(listing.id, status);
          break;
        case "WET_LEASE_AVAILABLE":
          await api.wetLeaseAvailable.confirmStatus(listing.id, status);
          break;
        case "WET_LEASE_WANTED":
          await api.wetLeaseWanted.confirmStatus(listing.id, status);
          break;
        case "WET_LEASE_AOG":
          await api.wetLeaseAOG.confirmStatus(listing.id, status);
          break;
        case "ENGINE_AVAILABLE":
          await api.engineSaleLeaseAvailable.confirmStatus(listing.id, status);
          break;
        case "ENGINE_WANTED":
          await api.engineSaleLeaseWanted.confirmStatus(listing.id, status);
          break;
        case "CHARTER_AVAILABLE":
          await api.aircraftCharterAvailable.confirmStatus(listing.id, status);
          break;
        case "CHARTER_WANTED":
          await api.aircraftCharterWanted.confirmStatus(listing.id, status);
          break;
        case "BUSINESS_JET_AVAILABLE":
          await api.businessJetAvailable.confirmStatus(listing.id, status);
          break;
        case "BUSINESS_JET_WANTED":
          await api.businessJetWanted.confirmStatus(listing.id, status);
          break;
      }
      setConfirmModal(null);
      await loadPendingListings();
    } catch (e) {
      console.error("Error confirming status", e);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredListings = useMemo(() => {
    if (selectedCategory === "ALL") return listings;
    return listings.filter((x) => x.moduleGroup === selectedCategory || x.category === selectedCategory);
  }, [listings, selectedCategory]);

  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {
      ALL: listings.length,
      Aircraft: 0,
      "Wet Lease": 0,
      Engine: 0,
      Charter: 0,
      "Business Jet": 0,
    };
    listings.forEach((l) => {
      if (counts[l.moduleGroup] !== undefined) {
        counts[l.moduleGroup]++;
      }
    });
    return counts;
  }, [listings]);

  const columns = useMemo(
    () => [
      {
        key: "moduleGroup",
        title: "Category",
        minWidth: 140,
        accessor: (r: UnifiedPendingListing) => r.moduleGroup,
        render: (_: any, r: UnifiedPendingListing) => {
          const colors: Record<string, { bg: string; color: string }> = {
            Aircraft: { bg: "#EFF6FF", color: "#1D4ED8" },
            "Wet Lease": { bg: "#FEF3C7", color: "#B45309" },
            Engine: { bg: "#F3E8FF", color: "#7E22CE" },
            Charter: { bg: "#ECFDF5", color: "#047857" },
            "Business Jet": { bg: "#FDF2F8", color: "#BE185D" },
          };
          const st = colors[r.moduleGroup] || { bg: "#F1F5F9", color: "#475569" };
          return (
            <div>
              <span
                style={{
                  background: st.bg,
                  color: st.color,
                  padding: "4px 10px",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  display: "inline-block",
                  marginBottom: 4,
                }}
              >
                {r.categoryLabel}
              </span>
            </div>
          );
        },
      },
      {
        key: "title",
        title: "Listing Title & Specs",
        minWidth: 260,
        accessor: (r: UnifiedPendingListing) => r.title,
        render: (_: any, r: UnifiedPendingListing) => (
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{r.title}</div>
            {r.subTitle && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{r.subTitle}</div>}
          </div>
        ),
      },
      {
        key: "company",
        title: "Company",
        minWidth: 180,
        accessor: (r: UnifiedPendingListing) => r.companyName,
        render: (_: any, r: UnifiedPendingListing) => (
          <div>
            <div style={{ fontWeight: 600, color: "#1E293B" }}>{r.companyName}</div>
            {r.userName && <div style={{ fontSize: 11, color: "#94A3B8" }}>By: {r.userName}</div>}
          </div>
        ),
      },
      {
        key: "createdDate",
        title: "Submitted On",
        minWidth: 140,
        accessor: (r: UnifiedPendingListing) => r.createdDate,
        render: (_: any, v: any) => {
          if (!v) return "-";
          const d = new Date(v);
          return (
            <div style={{ fontSize: 12, color: "#334155" }}>
              {d.toLocaleDateString()}
              <div style={{ fontSize: 10, color: "#94A3B8" }}>{d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
          );
        },
      },
      {
        key: "status",
        title: "Status",
        minWidth: 100,
        render: () => <Badge label="Pending Approval" color="#F59E0B" bg="#FEF3C7" />,
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 200,
        render: (r: UnifiedPendingListing) => (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Button
              icon={MdVisibility}
              variant="ghost"
              onClick={() => setSelectedDetail(r)}
            >
              View
            </Button>
            <Button
              icon={MdCheckCircle}
              loading={actionLoading === r.id}
              onClick={() => setConfirmModal({ listing: r, status: 1 })}
            >
              Approve
            </Button>
            <Button
              icon={MdCancel}
              variant="ghost"
              style={{ color: "#DC2626" }}
              onClick={() => setConfirmModal({ listing: r, status: 2 })}
            >
              Reject
            </Button>
          </div>
        ),
      },
    ],
    [actionLoading]
  );

  return (
    <div style={{ padding: "32px 40px", background: "#F8FAFC", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#0F172A", display: "flex", alignItems: "center", gap: 12 }}>
            <span>Pending Listings</span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                background: "#EF4444",
                color: "#fff",
                padding: "2px 10px",
                borderRadius: 20,
              }}
            >
              {listings.length}
            </span>
          </div>
          <div style={{ fontSize: 14, color: "#64748B", marginTop: 4 }}>
            Review and approve pending listings across all aviation modules.
          </div>
        </div>

        <Button icon={MdRefresh} onClick={loadPendingListings} loading={loading}>
          Refresh
        </Button>
      </div>

      {/* Module Filter Tabs */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          { key: "ALL", label: "All Pending" },
          { key: "Aircraft", label: "Aircraft Sale/Lease" },
          { key: "Wet Lease", label: "Wet Lease & AOG" },
          { key: "Engine", label: "Engine" },
          { key: "Charter", label: "Charter" },
          { key: "Business Jet", label: "Business Jet" },
        ].map((tab) => {
          const isSelected = selectedCategory === tab.key;
          const count = groupCounts[tab.key] || 0;
          return (
            <button
              key={tab.key}
              onClick={() => setSelectedCategory(tab.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 700,
                border: isSelected ? "2px solid #2563EB" : "1px solid #E2E8F0",
                background: isSelected ? "#EFF6FF" : "#FFFFFF",
                color: isSelected ? "#1D4ED8" : "#475569",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <span>{tab.label}</span>
              <span
                style={{
                  background: isSelected ? "#2563EB" : "#E2E8F0",
                  color: isSelected ? "#FFFFFF" : "#64748B",
                  fontSize: 11,
                  padding: "1px 7px",
                  borderRadius: 10,
                  fontWeight: 800,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #E2E8F0", overflow: "hidden" }}>
        <Table loading={loading} columns={columns} data={filteredListings} />
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <Modal open onClose={() => setConfirmModal(null)}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12, color: "#0F172A" }}>
            {confirmModal.status === 1 ? "Approve Listing" : "Reject Listing"}
          </div>
          <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.6 }}>
            Are you sure you want to update the status of listing <strong>{confirmModal.listing.title}</strong> (Company: <strong>{confirmModal.listing.companyName}</strong>) to{" "}
            <span style={{ fontWeight: 700, color: confirmModal.status === 1 ? "#16A34A" : "#DC2626" }}>
              {confirmModal.status === 1 ? "APPROVED" : "REJECTED"}
            </span>?
          </p>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
            <Button variant="ghost" onClick={() => setConfirmModal(null)}>
              Cancel
            </Button>
            <Button
              style={{
                background: confirmModal.status === 1 ? "#16A34A" : "#DC2626",
                color: "#fff",
              }}
              onClick={() => handleUpdateStatus(confirmModal.listing, confirmModal.status)}
            >
              {confirmModal.status === 1 ? "Yes, Approve" : "Yes, Reject"}
            </Button>
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {selectedDetail && (
        <Modal open fullScreen onClose={() => setSelectedDetail(null)}>
          <div style={{ padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, borderBottom: "1px solid #E2E8F0", paddingBottom: 16 }}>
              <div>
                <span
                  style={{
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    padding: "4px 12px",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {selectedDetail.categoryLabel}
                </span>
                <h1 style={{ fontSize: 24, fontWeight: 900, color: "#0F172A", margin: "10px 0 4px 0" }}>
                  {selectedDetail.title}
                </h1>
                <div style={{ color: "#64748B", fontSize: 13 }}>
                  Company: <strong>{selectedDetail.companyName}</strong> | Submitted by: {selectedDetail.userName ?? "N/A"}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <Button
                  icon={MdCheckCircle}
                  onClick={() => {
                    const l = selectedDetail;
                    setSelectedDetail(null);
                    setConfirmModal({ listing: l, status: 1 });
                  }}
                >
                  Approve
                </Button>
                <Button
                  icon={MdCancel}
                  variant="ghost"
                  style={{ color: "#DC2626" }}
                  onClick={() => {
                    const l = selectedDetail;
                    setSelectedDetail(null);
                    setConfirmModal({ listing: l, status: 2 });
                  }}
                >
                  Reject
                </Button>
              </div>
            </div>

            {/* Spec grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
              {Object.entries(selectedDetail.raw)
                .filter(
                  ([key, val]) =>
                    typeof val !== "object" &&
                    val !== null &&
                    val !== undefined &&
                    !["id", "confirmStatus", "isDeleted", "isActive"].includes(key)
                )
                .map(([key, val]) => (
                  <div
                    key={key}
                    style={{
                      background: "#F8FAFC",
                      padding: "12px 16px",
                      borderRadius: 12,
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>{key}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1E293B", marginTop: 4, wordBreak: "break-word" }}>
                      {String(val)}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
